import { motion } from "framer-motion";
import { useState } from "react";

export const C = {
  bg: "#0B0B0C",
  surface: "#141518",
  elev: "#1A1C20",
  graphite: "#2A2C30",
  ink: "#F2F0EB",
  ink2: "#9DA1A8",
  ink3: "#6E7178",
  orange: "#FF4D0A",
  lightBg: "#F2F0EB",
  lightInk: "#0B0B0C",
  lightInk2: "#55585E",
  lightInk3: "#8B8E93",
};

const EASE_OUT_EXPO = [0.22, 1, 0.36, 1];
const EASE_BACK = [0.34, 1.3, 0.64, 1];

const REVEAL_VIEWPORT = { once: true, margin: "-80px" };
const REVEAL_WHILE = { opacity: 1, y: 0 };

const MASKED_LINE_INITIAL = { y: "110%" };
const MASKED_LINE_ANIMATE = { y: 0 };

const CAPSULE_INITIAL = { width: 0 };
const CAPSULE_VIEWPORT = { once: true };

export const OrbitMark = ({ size = "0.72em", className = "" }) => (
  <svg viewBox="0 0 100 100" width={size} height={size} className={className} aria-hidden="true">
    <ellipse cx="50" cy="50" rx="46" ry="18" fill="none" stroke="currentColor" strokeWidth="5" transform="rotate(-24 50 50)" />
    <circle cx="50" cy="50" r="21" fill="#FF4D0A" />
    <circle cx="90" cy="32" r="6" fill="currentColor" />
    <circle cx="10" cy="69" r="4.5" fill="currentColor" opacity="0.55" />
  </svg>
);

export const Wordmark = ({ className = "" }) => {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <span
        className={`inline-flex items-center font-display font-extrabold uppercase tracking-tight text-[#F2F0EB] text-xl sm:text-2xl leading-none ${className}`}
        data-testid="wordmark-fallback"
      >
        SOURCECODED
      </span>
    );
  }
  return (
    <img
      src="/brand/sourcecoded-lockup-white.png"
      alt="SOURCECODED"
      className={`w-auto ${className}`}
      style={{ aspectRatio: "1791 / 155" }}
      loading="eager"
      fetchPriority="high"
      onError={(e) => {
        const el = e.currentTarget;
        if (!el.dataset.retried) {
          el.dataset.retried = "1";
          el.src = "/brand/sourcecoded-lockup-white.png?r=" + Date.now();
        } else {
          setFailed(true);
        }
      }}
    />
  );
};

export const Reveal = ({ children, delay = 0, y = 32, className = "", ...rest }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={REVEAL_WHILE}
    viewport={REVEAL_VIEWPORT}
    transition={{ duration: 0.9, delay, ease: EASE_OUT_EXPO }}
    {...rest}
  >
    {children}
  </motion.div>
);

export const Chapter = ({ num, label }) => (
  <div className="flex items-center gap-4 mb-8">
    {num && (
      <>
        <span className="font-mono-brand text-xs text-[#FF4D0A]">{num}</span>
        <span className="h-px w-10 bg-[#FF4D0A]/60" />
      </>
    )}
    <span className="eyebrow">{label}</span>
  </div>
);

export const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={MASKED_LINE_INITIAL}
      animate={MASKED_LINE_ANIMATE}
      transition={{ duration: 1.1, delay, ease: EASE_OUT_EXPO }}
    >
      {children}
    </motion.span>
  </span>
);

export const CapsuleBar = ({ value, color, delay = 0 }) => (
  <div className="capsule-track">
    <motion.div
      className="h-full"
      style={{
        background: `linear-gradient(90deg, ${color}55, ${color})`,
        boxShadow: `0 0 10px ${color}44`,
      }}
      initial={CAPSULE_INITIAL}
      whileInView={{ width: `${value}%` }}
      viewport={CAPSULE_VIEWPORT}
      transition={{ duration: 1.6, delay, ease: EASE_BACK }}
    />
  </div>
);

export const Code = ({ code, color }) => (
  <span
    className="font-mono-brand text-[9px] font-medium tracking-[0.15em] px-1.5 py-0.5"
    style={{ color, background: `${color}1a`, border: `1px solid ${color}33` }}
  >
    {code}
  </span>
);
